import {
  forwardRef, useEffect, useId, useMemo, useRef,
} from 'react';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import FormGroup from 'react-bootstrap/FormGroup';
import FormCheck from 'react-bootstrap/FormCheck';

/**
 * Форма события onChange у SimpleCheckboxInput.
 *
 * Компонент конструирует синтетическое событие поверх нативного, чтобы родительские
 * компоненты могли читать `event.target.checked` и `event.target.value` как обычно.
 *
 * `value` намеренно `any`: в binary это boolean, в triState — boolean | null;
 * потребители (в т.ч. ~17 SimpleCheckboxInput и Formik-обёртка CheckboxInput)
 * пишут `e.target.value` без сужения. `any` согласован с lenient tsconfig проекта
 * (`noImplicitAny: off`); ужесточение — отдельная задача вместе со strict-режимом
 * (по образцу select.types.ts → SelectChangeEvent).
 */
export interface SimpleCheckboxChangeEvent {
  target: {
    name?: string;
    value: any;
    checked: boolean;
  };
}

/**
 * Подписи состояний для `mode='triState'`.
 *
 * ВНИМАНИЕ: `undefined` — валидный идентификатор свойства (ключ), но keyword JS.
 * Доступ ТОЛЬКО через точечную нотацию `triStateLabels.undefined`; деструктуризация
 * `const { undefined } = labels` запрещена синтаксисом. Текущий код использует только
 * точечный доступ — оставляем как есть. Факт зафиксирован комментарием.
 */
interface TriStateLabels {
  yes: string;
  no: string;
  undefined: string;
}

/**
 * Пропсы «глупого» чекбокса (без Formik).
 *
 * Наследует нативные пропсы `<input>` через `...rest`. Через `Omit` убраны ключи с
 * конфликтующей семантикой:
 *  - `onChange` — у нас своя SimpleCheckboxChangeEvent;
 *  - `checked`  — поддерживает null для triState (indeterminate);
 *  - `type`     — всегда 'checkbox', не управляется снаружи.
 *
 * Остальные нативные пропсы (`id`, `autoFocus`, `aria-*`, ...) протекают к потребителю как есть.
 *
 * Formik-обёртка — CheckboxInput (CheckboxInput.tsx).
 *
 * `isNextField` (визуальное состояние «следующее обязательное поле») — подсветка обводкой
 * `.field-is-next`, гасится при `hasError` или `disabled` (см. CONTEXT.md → «Визуальные состояния поля»).
 */
export interface SimpleCheckboxInputProps
  extends Omit<React.ComponentProps<'input'>, 'onChange' | 'checked' | 'type'> {
  label?: React.ReactNode;
  /** null валиден только при mode='triState' (интерпретируется как indeterminate). */
  checked?: boolean | null;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  className?: string;
  rootClassName?: string;
  /** Колбэк вызывается, когда поле переходит в состояние ошибки валидации. */
  onValidateFail?(error: unknown): void;
  onChange?(event: SimpleCheckboxChangeEvent): void;
  noMarginBottom?: boolean;
  labelClassName?: string;
  name?: string;
  error?: unknown;
  mode?: 'binary' | 'triState';
  triStateLabels?: TriStateLabels;
  hoverable?: boolean;
  /** Подсветить поле как «следующее для заполнения» (`.field-is-next`); гасится при ошибке или `disabled`. */
  isNextField?: boolean;
}

const SimpleCheckboxInput = forwardRef<HTMLInputElement, SimpleCheckboxInputProps>((props, ref) => {

  const {
    label,
    checked,
    disabled,
    readOnly,
    required,
    className,
    rootClassName,
    onValidateFail,
    onChange,
    noMarginBottom,
    labelClassName,
    name,
    error,
    mode = 'binary',
    triStateLabels = { yes: 'Да', no: 'Нет', undefined: 'Не определено' },
    hoverable,
    isNextField,
    ...rest
  } = props;

  const internalRef = useRef<HTMLInputElement>(null);
  // ForwardedRef включает ветку RefCallback (без .current), а ниже мы читаем .current
  // для indeterminate — поэтому приводим к object-ref. Callback-ref никто не передаёт
  // (прямых ref-передач в потребителях нет — см. CHECKBOX_TSX_PLAN.md, риск 2).
  // Runtime-безопасно: даже при callback-ref проверка `if (el)` ниже просто пропустит запись.
  const combinedRef = (ref || internalRef) as React.RefObject<HTMLInputElement>;

  // Связываем input ↔ label через id — иначе FormCheck.Label рендерит пустой htmlFor,
  // и клик по тексту чекбокса не переключает значение. id нужно передавать на FormCheck
  // (он прокидывает его в FormContext.controlId, откуда берут input и label).
  // См. комментарий в CheckboxInput.tsx.
  const autoId = useId();
  const inputId = rest.id || autoId;

  const isRequired = required;
  const hasError = !isEmpty(error);

  // Определяем текущее значение на основе пропса checked
  const currentValue = useMemo<boolean | null>(() => {
    if (mode === 'binary') return !!checked;
    if (checked === null || checked === undefined) return null;
    return checked === true ? true : false;
  }, [checked, mode]);

  // Логика переключения значений
  const getNextValue = (current: boolean | null): boolean | null => {
    if (mode === 'binary') return !current;
    if (current === null || current === undefined) return true;
    if (current === true) return false;
    return null;
  };

  // Управление визуальным состоянием indeterminate для чекбокса
  useEffect(() => {
    const el = combinedRef?.current;
    if (el) {
      el.indeterminate = mode === 'triState' && currentValue === null;
    }
  }, [currentValue, mode, combinedRef]);

  // Логика валидации (из оригинального компонента)
  useEffect(() => {
    if (hasError && (typeof onValidateFail === 'function')) {
      onValidateFail(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  // Формирование отображаемого лейбла для triState
  const displayLabel = useMemo(() => {
    if (!label) return label;
    if (mode !== 'triState') return label;

    if (currentValue === true) return `${ label }: ${ triStateLabels.yes }`;
    if (currentValue === false) return `${ label }: ${ triStateLabels.no }`;
    return `${ label }: ${ triStateLabels.undefined }`;
  }, [label, mode, currentValue, triStateLabels]);

  // Обработчик изменений
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = getNextValue(currentValue);

    if (typeof onChange === 'function') {
      // Создаем синтетическое событие, чтобы родительские компоненты
      // могли читать event.target.checked и event.target.value как обычно
      const syntheticEvent: SimpleCheckboxChangeEvent = {
        ...event,
        target: {
          ...event.target,
          name: name,
          value: newValue,
          checked: newValue === true, // Для binary режима это всегда корректный boolean
        },
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <FormGroup className={ cx(noMarginBottom && 'no-margin-bottom', rootClassName, hasError && 'field-has-error', isNextField && !hasError && !disabled && 'field-is-next') }>
      <FormCheck
        className={ cx('core-checkbox', hoverable && 'hoverable') }
        id={ inputId }
      >
        <FormCheck.Input
          { ...rest }
          ref={ combinedRef }
          type='checkbox'
          checked={ currentValue === true }
          disabled={ disabled }
          readOnly={ readOnly }
          className={ cx(className, { required: isRequired }) }
          name={ name }
          aria-checked={ mode === 'triState' ? (currentValue === null ? 'mixed' : currentValue) : undefined }
          aria-invalid={ hasError || undefined }
          aria-required={ isRequired || undefined }
          onChange={ handleChange }
        />
        <FormCheck.Label className={ labelClassName }>
          { displayLabel }
        </FormCheck.Label>
      </FormCheck>
    </FormGroup>
  );
});

SimpleCheckboxInput.displayName = 'SimpleCheckboxInput';

export default SimpleCheckboxInput;
